import router from 'next/router'
import apiClient from 'helpers/api-client'
import { getIdToken } from 'helpers/auth'
import { getActiveOrgId } from 'helpers/team-access'
import { API_TEAM } from 'routes/apis'
import { useActiveOrgStore } from '~/domains/Organization/store'
import { DataResponse } from '~/@types/commons'
import {
  GetMemberListParams,
  GetMemberListResponse,
  GetRolesResponse,
  InviteMemberParams,
  InviteMemberResponse,
  MemberDetail,
  MyPermissionsResponse,
  PermissionsMatrixResponse,
  UpdateMemberParams,
  UpdateMemberResponse,
} from './interface'

export const getMemberList = async (
  params: GetMemberListParams,
): Promise<GetMemberListResponse> => {
  const res = await apiClient.get<
    DataResponse<GetMemberListResponse>
  >(API_TEAM.members.list, { params })
  return res.data.data
}

/**
 * The bare GET /me request, with no stale-org recovery — see getMyPermissions.
 *
 * The token is attached explicitly rather than left to the api-client default,
 * which setSession() only populates from initialize()'s effect: AuthProvider
 * queries /me during its first render, before that effect runs, so otherwise
 * the request 401s and only succeeds on the interceptor's refresh-retry. Same
 * pattern as getUserProfile in AuthContext.
 *
 * @param orgId Overrides the Org-Id the api-client stamps; omit for the active org.
 */
const requestMe = async (
  orgId?: string,
): Promise<MyPermissionsResponse> => {
  const res = await apiClient.get<DataResponse<MyPermissionsResponse>>(
    API_TEAM.me,
    {
      headers: {
        Authorization: getIdToken(),
        ...(orgId ? { 'Org-Id': orgId } : {}),
      },
    },
  )
  return res.data.data
}

/**
 * GET /me — who the current user is in the active org and what they may do.
 *
 * Self-heals a stale active org. Auth is cross-platform but the org id is only
 * cleared by a logout that happens here, so signing in as another account
 * elsewhere leaves us stamping an org this user cannot access. On a 403 the id
 * is dropped and the page reloaded: that id was stamped on the whole first
 * request wave, and nothing refetches on an org change, so a reload is the only
 * recovery path — the same reason switchOrg's caller reloads. It cannot loop,
 * since the id is gone before the reload.
 *
 * @param orgId Pins the call to one org without changing the active one, to
 * validate a target before committing an org switch. Such a call is excluded
 * from the recovery above: switchOrg reads a 403 as "that target org is not
 * accessible", and clearing the active org there would discard a good one.
 */
export const getMyPermissions = async (
  orgId?: string,
): Promise<MyPermissionsResponse> => {
  // Guarded against non-strings: a queryFn passed by reference is called with
  // React Query's context object, which would ship as "[object Object]".
  const scopedToOrg = typeof orgId === 'string' && orgId !== ''

  try {
    return await requestMe(scopedToOrg ? orgId : undefined)
  } catch (error) {
    if (
      scopedToOrg ||
      error?.response?.status !== 403 ||
      !getActiveOrgId()
    ) {
      throw error
    }
    useActiveOrgStore.getState().clearActiveOrg()
    router.reload()

    // Resolving keeps AuthGuard's 403 page off screen until the reload lands.
    return requestMe()
  }
}

export const getPermissionsMatrix =
  async (): Promise<PermissionsMatrixResponse> => {
    const res = await apiClient.get<
      DataResponse<PermissionsMatrixResponse>
    >(API_TEAM.permissions.matrix)
    return res.data.data
  }

// Role-centric list (each role carries its own full permission catalog) — the
// source for the invite/edit role picker + customize-role toggles. Distinct
// from getPermissionsMatrix, which PermissionMatrix's compare-all-roles view
// still uses.
export const getRoles = async (): Promise<GetRolesResponse> => {
  const res = await apiClient.get<DataResponse<GetRolesResponse>>(
    API_TEAM.roles,
  )
  return res.data.data
}

export const inviteMember = async (
  params: InviteMemberParams,
): Promise<InviteMemberResponse> => {
  const res = await apiClient.post<
    DataResponse<InviteMemberResponse>
  >(API_TEAM.members.invite, params)
  return res.data.data
}

// Delete and GET-detail share the same /members/{mask_id} path; the delete
// takes no body and its success body is not consumed.
export const deleteMember = async (
  memberMaskId: string,
): Promise<void> => {
  await apiClient.delete(API_TEAM.members.detail(memberMaskId))
}

// Resend a pending member's setup/invitation link. The member is identified
// entirely by the path; the server mints a fresh token + expiry, so no client
// body is sent (the saved Postman body's token/expiry fields are
// server-generated artifacts, not client inputs).
export const resendInvite = async (
  memberMaskId: string,
): Promise<void> => {
  await apiClient.post(API_TEAM.members.resendInvite(memberMaskId))
}

export const getMemberDetail = async (
  memberMaskId: string,
): Promise<MemberDetail> => {
  const res = await apiClient.get<DataResponse<MemberDetail>>(
    API_TEAM.members.detail(memberMaskId),
  )
  return res.data.data
}

export const updateMember = async ({
  memberMaskId,
  ...body
}: UpdateMemberParams): Promise<UpdateMemberResponse> => {
  const res = await apiClient.put<DataResponse<UpdateMemberResponse>>(
    API_TEAM.members.detail(memberMaskId),
    body,
  )
  return res.data.data
}
